/**
 * Mock API kliens — a NestJS backend megérkeztéig in-memory szolgálja az adatokat.
 * A kontraktus a docs/04-api.md V1 szerződésével egyezik.
 */

import { delay } from '../utils'
import { generateMockInvoicePdf } from '../pdf/mockPdfGenerator'
import { buildInvoiceDetail, FIXTURES } from './fixtures'
import type {
  AccountantExport,
  AiAccountantPrep,
  AiCashflow,
  AiMonthlySummary,
  AiSuggestion,
  AuditLogEntry,
  AuthSession,
  BankingImportResponse,
  Company,
  CompanyMembership,
  CompanySettings,
  CreateUserPayload,
  DiscrepancyIssue,
  DiscrepancyType,
  EmailAccountSettings,
  EmailInboxItem,
  ExtractResponse,
  HealthIntegrationsResponse,
  ImapFoldersResult,
  InvoiceComment,
  InvoiceDetail,
  InvoiceListItem,
  JobResponse,
  JobRun,
  LinkDownloadResponse,
  ListInvoicesParams,
  NavCredentials,
  Notification,
  OutgoingEmailSettings,
  PaginatedResponse,
  RecordPaymentResponse,
  Role,
  SzamlazzCredentialsStatus,
  UpdateCompanySettingsPayload,
  UpdateEmailAccountPayload,
  UpdateNavCredentialsPayload,
  UpdateOutgoingEmailPayload,
  UploadInvoicePayload,
  UploadInvoiceResponse,
  UpsertCompanyPayload,
  User,
  UserListItem,
  VatMonthlySummary,
  WorkflowStatus,
} from './types'

/** Mutálható állapot — fejlesztéshez. Production-ben fetch fog jönni a backend felé. */
const state = {
  invoices: structuredClone(FIXTURES.INVOICES) as InvoiceListItem[],
  notifications: structuredClone(FIXTURES.NOTIFICATIONS) as Notification[],
  exportPackage: structuredClone(FIXTURES.EXPORT_PACKAGE) as AccountantExport,
  companies: Object.values(structuredClone(FIXTURES.COMPANIES)) as Company[],
  auditLogs: [] as AuditLogEntry[],
  users: Object.values(structuredClone(FIXTURES.USERS)) as User[],
  memberships: structuredClone(FIXTURES.MEMBERSHIPS),
  navCredentials: new Map<string, NavCredentials>(
    Object.values(FIXTURES.COMPANIES).map((c) => [
      c.id,
      {
        companyId: c.id,
        navUserLogin: 'adaptivo_tech',
        navEnvironment: 'production' as const,
        hasPassword: true,
        hasSigningKey: true,
        hasExchangeKey: true,
        lastUsedAt: '2026-06-03T04:00:00Z',
        lastRotatedAt: '2026-04-15T10:00:00Z',
        lastPingAt: '2026-06-03T04:00:00Z',
        lastPingStatus: 'ok' as const,
        lastPingError: null,
      },
    ]),
  ),
  emailAccount: {
    id: 'em-1',
    companyId: null,
    address: 'szamla@adaptivo.hu',
    protocol: 'imap' as const,
    imapHost: 'imap.adaptivo.hu',
    imapPort: 993,
    imapUsername: 'szamla@adaptivo.hu',
    imapFolder: 'INBOX',
    hasPassword: true,
    useOauth: false,
    status: 'active' as const,
    lastSyncAt: '2026-06-03T12:45:00Z',
    errorMessage: null,
  } as EmailAccountSettings,
  companySettings: new Map<string, CompanySettings>(
    Object.values(FIXTURES.COMPANIES).map((c) => [
      c.id,
      {
        companyId: c.id,
        accountantName: 'Szabó Gábor (KKV-Könyv Kft.)',
        accountantEmail: 'konyvelo@kkv-konyv.hu',
        exportFormat: 'excel' as const,
        vatPeriod: 'monthly' as const,
        monthlyExportDay: 1,
        monthlyExportHour: 6,
        defaultExchangeRateSource: 'MNB' as const,
      },
    ]),
  ),
  comments: new Map<string, InvoiceComment[]>(),
  outgoingEmail: {
    id: 'oe-1',
    provider: 'smtp' as const,
    fromAddress: '',
    fromName: null,
    replyToAddress: null,
    smtpHost: null,
    smtpPort: 587,
    smtpUsername: null,
    hasPassword: false,
    encryption: 'starttls' as const,
    enabled: false,
    lastTestAt: null,
    lastTestStatus: null,
    lastTestError: null,
  } as OutgoingEmailSettings,
  /** "Bejelentkezett" user a session-context-hez (audit logba) — frissítjük login-kor. */
  currentActor: null as { id: string; name: string; role: Role | null } | null,
}

// --- localStorage persistence (a teljes mock state-et eltároljuk frissítés után) ---

// v2: fixture kiürítés — minden böngésző tisztán indul, csak az első user
// hozza létre a tényleges adat-kezdést a Setup wizard-on át.
const STORAGE_KEY = 'adaptivo.szamla.mockState.v2'
// A korábbi (v1) state-et explicit eltávolítjuk hogy ne kavarjon.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('adaptivo.szamla.mockState.v1')
  } catch {
    // noop
  }
}

function serializeMaps(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: Array.from(value.entries()) }
  }
  return value
}

function reviveMaps(_key: string, value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    (value as { __type?: string }).__type === 'Map' &&
    Array.isArray((value as { entries?: unknown }).entries)
  ) {
    return new Map((value as { entries: [unknown, unknown][] }).entries)
  }
  return value
}

let saveDebounce: ReturnType<typeof setTimeout> | null = null
function persistState() {
  if (typeof window === 'undefined') return
  if (saveDebounce) clearTimeout(saveDebounce)
  saveDebounce = setTimeout(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state, serializeMaps))
    } catch {
      // quota, JSON-cycle stb. — silently
    }
  }, 200)
}

function loadPersistedState() {
  if (typeof window === 'undefined') return
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const restored = JSON.parse(raw, reviveMaps) as Partial<typeof state>
    // egyenként mergeljük, hogy ha új mezőket vezetünk be, a default fixtures érvényesüljenek
    if (Array.isArray(restored.invoices)) state.invoices = restored.invoices
    if (Array.isArray(restored.notifications)) state.notifications = restored.notifications
    if (restored.exportPackage) state.exportPackage = restored.exportPackage
    if (Array.isArray(restored.companies)) state.companies = restored.companies
    if (Array.isArray(restored.auditLogs)) state.auditLogs = restored.auditLogs
    if (Array.isArray(restored.users)) state.users = restored.users
    if (restored.memberships) state.memberships = restored.memberships
    if (restored.comments instanceof Map) state.comments = restored.comments
    if (restored.navCredentials instanceof Map) state.navCredentials = restored.navCredentials
    if (restored.emailAccount) state.emailAccount = restored.emailAccount
    if (restored.outgoingEmail) state.outgoingEmail = restored.outgoingEmail
    if (restored.companySettings instanceof Map) state.companySettings = restored.companySettings
  } catch {
    // korrupt LS — ignorálva, fixture marad
  }
}

loadPersistedState()

/** Clear mock state — fejlesztési segéd; a UI nem hívja. */
export function resetMockState() {
  if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY)
  window.location.reload()
}

// --- Audit log helper ---

function audit(entry: Omit<AuditLogEntry, 'id' | 'tenantId' | 'createdAt'>) {
  state.auditLogs.unshift({
    id: 'au-' + Math.random().toString(36).slice(2, 10),
    tenantId: FIXTURES.TENANT_ID,
    createdAt: new Date().toISOString(),
    ...entry,
  })
  if (state.auditLogs.length > 500) state.auditLogs.length = 500
  persistState()
}

function applyFilters(items: InvoiceListItem[], params: ListInvoicesParams): InvoiceListItem[] {
  return items.filter((i) => {
    if (params.from && i.issueDate && i.issueDate < params.from) return false
    if (params.to && i.issueDate && i.issueDate > params.to) return false
    if (params.status && params.status.length > 0 && !params.status.includes(i.workflowStatus)) return false
    if (params.matchingStatus?.length && !params.matchingStatus.includes(i.matchingStatus)) return false
    if (params.source?.length && !params.source.includes(i.source)) return false
    if (params.countryType?.length && !params.countryType.includes(i.countryType)) return false
    if (params.paymentStatus?.length && !params.paymentStatus.includes(i.paymentStatus)) return false
    if (params.supplierTaxNumber && i.supplierTaxNumber !== params.supplierTaxNumber) return false
    if (
      params.invoiceNumber &&
      i.invoiceNumber &&
      !i.invoiceNumber.toLowerCase().includes(params.invoiceNumber.toLowerCase())
    )
      return false
    if (params.amountMin !== undefined && (i.grossAmount ?? 0) < params.amountMin) return false
    if (params.amountMax !== undefined && (i.grossAmount ?? 0) > params.amountMax) return false
    if (params.hasDiscrepancy) {
      const discrepancyStatuses: WorkflowStatus[] = ['discrepancy', 'under_review']
      if (!discrepancyStatuses.includes(i.workflowStatus)) return false
    }
    if (params.tags && params.tags.length > 0) {
      const itemTags = new Set((i.tags ?? []).map((t) => t.toLowerCase()))
      const allMatch = params.tags.every((t) => itemTags.has(t.toLowerCase()))
      if (!allMatch) return false
    }
    return true
  })
}

function normalizeTag(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase().slice(0, 40)
}

export const mockApi = {
  async needsSetup(): Promise<boolean> {
    await delay(40)
    return state.users.length === 0
  },

  async setupInitialAdmin(payload: {
    fullName: string
    email: string
    password: string
    companyLegalName: string
    companyTaxNumber: string
    companyCountryCode?: string
    companyCurrency?: string
  }): Promise<AuthSession> {
    await delay(300)
    if (state.users.length > 0) {
      throw makeProblem(409, 'setup.already_done', 'Az induló setup már megtörtént.')
    }
    const email = payload.email.trim().toLowerCase()
    const fullName = payload.fullName.trim()
    const legal = payload.companyLegalName.trim()
    const tax = payload.companyTaxNumber.trim()
    if (!email.includes('@')) throw makeProblem(422, 'setup.invalid_email', 'Érvénytelen email cím')
    if (!fullName) throw makeProblem(422, 'setup.invalid_name', 'A teljes név kötelező')
    if (!legal) throw makeProblem(422, 'setup.invalid_company', 'A cég neve kötelező')
    if (!tax) throw makeProblem(422, 'setup.invalid_tax', 'Az adószám kötelező')

    const company: Company = {
      id: 'co-' + Math.random().toString(36).slice(2, 10),
      tenantId: FIXTURES.TENANT_ID,
      legalName: legal,
      taxNumber: tax,
      countryCode: payload.companyCountryCode || 'HU',
      defaultCurrency: payload.companyCurrency || 'HUF',
    }
    state.companies.push(company)
    state.navCredentials.set(company.id, {
      companyId: company.id,
      navUserLogin: '',
      navEnvironment: 'production',
      hasPassword: false,
      hasSigningKey: false,
      hasExchangeKey: false,
      lastUsedAt: null,
      lastRotatedAt: null,
      lastPingAt: null,
      lastPingStatus: null,
      lastPingError: null,
    })
    state.companySettings.set(company.id, {
      companyId: company.id,
      accountantName: null,
      accountantEmail: null,
      exportFormat: 'excel',
      vatPeriod: 'monthly',
      monthlyExportDay: 1,
      monthlyExportHour: 6,
      defaultExchangeRateSource: 'MNB',
    })

    const user: User = {
      id: 'u-' + Math.random().toString(36).slice(2, 10),
      tenantId: FIXTURES.TENANT_ID,
      email,
      fullName,
      mfaEnabled: false,
      status: 'active',
      lastLoginAt: new Date().toISOString(),
    }
    state.users.push(user)
    const memberships: CompanyMembership[] = [
      { companyId: company.id, legalName: company.legalName, role: 'admin' },
    ]
    state.memberships[user.id] = memberships
    state.currentActor = { id: user.id, name: user.fullName, role: 'admin' }

    audit({
      actorType: 'user',
      actorId: user.id,
      actorName: user.fullName,
      actorRole: 'admin',
      subjectType: 'setup',
      subjectId: null,
      action: 'setup.initial_admin_created',
      outcome: 'success',
      metadata: { email, company: company.legalName, taxNumber: company.taxNumber },
    })
    persistState()

    return {
      user,
      tenantId: FIXTURES.TENANT_ID,
      memberships,
      expiresAt: new Date(Date.now() + 8 * 3600 * 1000).toISOString(),
    }
  },

  // Ez az öt metódus eddig CSAK a valódi kliensben létezett, és a UI közvetlenül
  // `fetchApi`-ból hívta őket (`AuthProvider`, `SetPasswordPage`) — a mockot megkerülve.
  // Amióta a szerződés a valódi kliensből származik, a mocknak is teljesítenie kell.
  // A meghívós flow SMTP-t és backendet igényel, azt mockban nem szimuláljuk: látható
  // 501-et adunk, nem hamis sikert.
  async logout(): Promise<void> {
    await delay(60)
    state.currentActor = null
    persistState()
  },

  async me(): Promise<AuthSession> {
    await delay(40)
    throw makeProblem(501, 'auth.me_not_in_mock', 'A session-visszaolvasás csak valódi backenddel működik.')
  },

  async validateInviteToken(_token: string): Promise<{ email: string; fullName: string }> {
    await delay(60)
    throw makeProblem(501, 'invite.not_in_mock', 'A meghívós flow csak valódi backenddel működik.')
  },

  async acceptInvite(_token: string, _password: string): Promise<AuthSession> {
    await delay(60)
    throw makeProblem(501, 'invite.not_in_mock', 'A meghívós flow csak valódi backenddel működik.')
  },

  async resendInvite(_userId: string): Promise<void> {
    await delay(60)
    throw makeProblem(501, 'invite.not_in_mock', 'A meghívó újraküldése csak valódi backenddel működik.')
  },

  async login(email: string, _password: string): Promise<AuthSession> {
    await delay(300)
    const user = state.users.find((u) => u.email.toLowerCase() === email.toLowerCase())
    if (!user) {
      audit({
        actorType: 'system',
        actorId: null,
        actorName: null,
        actorRole: null,
        subjectType: 'auth',
        subjectId: null,
        action: 'auth.login_failed',
        outcome: 'failure',
        metadata: { email },
      })
      throw makeProblem(401, 'auth.unauthenticated', 'Ismeretlen email vagy jelszó')
    }
    if (user.status !== 'active') {
      throw makeProblem(403, 'auth.disabled', 'A felhasználó nem aktív')
    }
    const memberships = state.memberships[user.id] ?? []
    const firstRole = memberships[0]?.role ?? null
    state.currentActor = { id: user.id, name: user.fullName, role: firstRole }
    user.lastLoginAt = new Date().toISOString()
    audit({
      actorType: 'user',
      actorId: user.id,
      actorName: user.fullName,
      actorRole: firstRole,
      subjectType: 'auth',
      subjectId: user.id,
      action: 'auth.login',
      outcome: 'success',
      metadata: null,
    })
    return {
      user,
      tenantId: FIXTURES.TENANT_ID,
      memberships,
      expiresAt: new Date(Date.now() + 8 * 3600 * 1000).toISOString(),
    }
  },

  async listUsers(): Promise<UserListItem[]> {
    await delay(80)
    return state.users.map((u) => ({
      id: u.id,
      fullName: u.fullName,
      email: u.email,
      mfaEnabled: u.mfaEnabled,
      status: u.status,
      lastLoginAt: u.lastLoginAt ?? null,
      memberships: (state.memberships[u.id] ?? []).map((m) => ({
        companyId: m.companyId,
        legalName: m.legalName,
        role: m.role,
      })),
    }))
  },

  async createUser(payload: CreateUserPayload): Promise<UserListItem> {
    await delay(220)
    const email = payload.email.trim().toLowerCase()
    if (!email || !email.includes('@')) {
      throw makeProblem(422, 'user.invalid_email', 'Érvénytelen email cím')
    }
    if (state.users.some((u) => u.email.toLowerCase() === email)) {
      throw makeProblem(409, 'user.email_taken', 'Ez az email cím már létezik')
    }
    const company = state.companies.find((c) => c.id === payload.companyId)
    if (!company) {
      throw makeProblem(404, 'company.not_found', 'A cég nem található')
    }
    const id = 'u-' + Math.random().toString(36).slice(2, 10)
    const user: User = {
      id,
      tenantId: FIXTURES.TENANT_ID,
      email,
      fullName: payload.fullName.trim() || email.split('@')[0],
      mfaEnabled: false,
      status: 'active',
      lastLoginAt: undefined,
    }
    state.users.push(user)
    state.memberships[id] = [
      { companyId: company.id, legalName: company.legalName, role: payload.role },
    ]
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'user',
      subjectId: id,
      action: 'user.created',
      outcome: 'success',
      metadata: { email: user.email, role: payload.role, companyId: company.id, companyName: company.legalName },
    })

    // Meghívó küldése — csak ha kérik ÉS a kimenő email konfigurált és bekapcsolt.
    if (payload.sendInvite) {
      const oe = state.outgoingEmail
      const canSend =
        oe.enabled &&
        oe.fromAddress.trim() &&
        (oe.provider !== 'smtp' || (oe.smtpHost?.trim() && oe.smtpUsername?.trim() && oe.hasPassword))
      if (canSend) {
        audit({
          actorType: 'user',
          actorId: state.currentActor?.id ?? null,
          actorName: state.currentActor?.name ?? null,
          actorRole: state.currentActor?.role ?? null,
          subjectType: 'user',
          subjectId: id,
          action: 'user.invite_sent',
          outcome: 'success',
          metadata: { to: user.email, from: oe.fromAddress, provider: oe.provider },
        })
      } else {
        audit({
          actorType: 'system',
          actorId: null,
          actorName: null,
          actorRole: null,
          subjectType: 'user',
          subjectId: id,
          action: 'user.invite_skipped',
          outcome: 'info',
          metadata: {
            reason: 'outgoing_email_not_configured',
            email: user.email,
          },
        })
      }
    }

    persistState()
    return {
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      mfaEnabled: user.mfaEnabled,
      status: user.status,
      lastLoginAt: null,
      memberships: state.memberships[id].map((m) => ({
        companyId: m.companyId,
        legalName: m.legalName,
        role: m.role,
      })),
    }
  },

  async listCompanies(): Promise<Company[]> {
    await delay(80)
    return [...state.companies]
  },

  async upsertCompany(payload: UpsertCompanyPayload): Promise<Company> {
    await delay(180)
    const legal = payload.legalName.trim()
    const tax = payload.taxNumber.trim()
    if (!legal) throw makeProblem(422, 'company.invalid_name', 'A cég neve kötelező')
    if (!tax) throw makeProblem(422, 'company.invalid_tax', 'Az adószám kötelező')

    if (payload.id) {
      const idx = state.companies.findIndex((c) => c.id === payload.id)
      if (idx < 0) throw makeProblem(404, 'company.not_found', 'A cég nem található')
      const updated: Company = {
        ...state.companies[idx],
        legalName: legal,
        taxNumber: tax,
        countryCode: payload.countryCode || state.companies[idx].countryCode,
        defaultCurrency: payload.defaultCurrency || state.companies[idx].defaultCurrency,
        logoDataUrl:
          payload.logoDataUrl === undefined ? state.companies[idx].logoDataUrl ?? null : payload.logoDataUrl,
      }
      state.companies[idx] = updated
      audit({
        actorType: 'user',
        actorId: state.currentActor?.id ?? null,
        actorName: state.currentActor?.name ?? null,
        actorRole: state.currentActor?.role ?? null,
        subjectType: 'company',
        subjectId: updated.id,
        action: 'company.updated',
        outcome: 'success',
        metadata: { legalName: updated.legalName, taxNumber: updated.taxNumber },
      })
      persistState()
      return updated
    }

    if (state.companies.some((c) => c.taxNumber.toLowerCase() === tax.toLowerCase())) {
      throw makeProblem(409, 'company.tax_taken', 'Ezzel az adószámmal már létezik cég')
    }
    const created: Company = {
      id: 'co-' + Math.random().toString(36).slice(2, 10),
      tenantId: FIXTURES.TENANT_ID,
      legalName: legal,
      taxNumber: tax,
      countryCode: payload.countryCode || 'HU',
      defaultCurrency: payload.defaultCurrency || 'HUF',
      logoDataUrl: payload.logoDataUrl ?? null,
    }
    state.companies.push(created)
    state.companySettings.set(created.id, {
      companyId: created.id,
      accountantName: null,
      accountantEmail: null,
      exportFormat: 'excel',
      vatPeriod: 'monthly',
      monthlyExportDay: 1,
      monthlyExportHour: 6,
      defaultExchangeRateSource: 'MNB',
    })
    state.navCredentials.set(created.id, {
      companyId: created.id,
      navUserLogin: '',
      navEnvironment: 'production',
      hasPassword: false,
      hasSigningKey: false,
      hasExchangeKey: false,
      lastUsedAt: null,
      lastRotatedAt: null,
      lastPingAt: null,
      lastPingStatus: null,
      lastPingError: null,
    })
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'company',
      subjectId: created.id,
      action: 'company.created',
      outcome: 'success',
      metadata: { legalName: created.legalName, taxNumber: created.taxNumber },
    })
    persistState()
    return created
  },

  async listAuditLogs(opts?: { limit?: number; subjectType?: string }): Promise<AuditLogEntry[]> {
    await delay(80)
    const items = opts?.subjectType
      ? state.auditLogs.filter((e) => e.subjectType === opts.subjectType)
      : state.auditLogs
    return items.slice(0, opts?.limit ?? 200)
  },

  async listInvoices(params: ListInvoicesParams): Promise<PaginatedResponse<InvoiceListItem>> {
    await delay(150)
    const filtered = applyFilters(state.invoices, params)
    const page = params.page ?? 1
    const pageSize = Math.min(params.pageSize ?? 50, 200)
    const start = (page - 1) * pageSize
    const items = filtered.slice(start, start + pageSize)
    return { items, meta: { total: filtered.length, page, pageSize } }
  },

  async getInvoice(id: string): Promise<InvoiceDetail> {
    await delay(120)
    const item = state.invoices.find((i) => i.id === id)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    return buildInvoiceDetail(item)
  },

  async getInvoicePdf(id: string): Promise<Uint8Array> {
    await delay(200)
    const item = state.invoices.find((i) => i.id === id)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    if (!item.hasPdf) throw makeProblem(404, 'file.not_found', 'Ehhez a számlához nincs PDF')
    const detail = buildInvoiceDetail(item)
    return generateMockInvoicePdf(detail)
  },

  async updateInvoiceStatus(id: string, toStatus: WorkflowStatus, reason?: string): Promise<InvoiceListItem> {
    await delay(150)
    const item = state.invoices.find((i) => i.id === id)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    const fromStatus = item.workflowStatus
    item.workflowStatus = toStatus
    if (toStatus === 'approved') item.approvalStatus = 'approved'
    if (toStatus === 'rejected') item.approvalStatus = 'rejected'
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'invoice',
      subjectId: id,
      action: 'invoice.status_changed',
      outcome: 'success',
      metadata: { from: fromStatus, to: toStatus, reason: reason ?? null, invoiceNumber: item.invoiceNumber },
    })
    persistState()
    return item
  },

  async approveInvoice(id: string, comment?: string): Promise<InvoiceListItem> {
    await delay(180)
    const item = state.invoices.find((i) => i.id === id)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    item.workflowStatus = 'approved'
    item.approvalStatus = 'approved'
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'invoice',
      subjectId: id,
      action: 'invoice.approved',
      outcome: 'success',
      metadata: { invoiceNumber: item.invoiceNumber, comment: comment ?? null },
    })
    persistState()
    return item
  },

  async archiveInvoice(id: string, reason: string): Promise<void> {
    await delay(120)
    const item = state.invoices.find((i) => i.id === id)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    item.workflowStatus = 'archived'
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'invoice',
      subjectId: id,
      action: 'invoice.archived',
      outcome: 'success',
      metadata: { invoiceNumber: item.invoiceNumber, reason },
    })
    persistState()
  },

  async addInvoiceTag(invoiceId: string, tag: string): Promise<InvoiceListItem> {
    await delay(80)
    const item = state.invoices.find((i) => i.id === invoiceId)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    const normalized = normalizeTag(tag)
    if (!normalized) throw makeProblem(422, 'tag.invalid', 'Üres címke nem rögzíthető')
    if (!Array.isArray(item.tags)) item.tags = []
    if (!item.tags.some((t) => t.toLowerCase() === normalized)) {
      item.tags.push(normalized)
      audit({
        actorType: 'user',
        actorId: state.currentActor?.id ?? null,
        actorName: state.currentActor?.name ?? null,
        actorRole: state.currentActor?.role ?? null,
        subjectType: 'invoice',
        subjectId: invoiceId,
        action: 'invoice.tag_added',
        outcome: 'success',
        metadata: { tag: normalized, invoiceNumber: item.invoiceNumber },
      })
      persistState()
    }
    return item
  },

  async removeInvoiceTag(invoiceId: string, tag: string): Promise<InvoiceListItem> {
    await delay(80)
    const item = state.invoices.find((i) => i.id === invoiceId)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    const normalized = normalizeTag(tag)
    const before = item.tags?.length ?? 0
    item.tags = (item.tags ?? []).filter((t) => t.toLowerCase() !== normalized)
    if ((item.tags?.length ?? 0) !== before) {
      audit({
        actorType: 'user',
        actorId: state.currentActor?.id ?? null,
        actorName: state.currentActor?.name ?? null,
        actorRole: state.currentActor?.role ?? null,
        subjectType: 'invoice',
        subjectId: invoiceId,
        action: 'invoice.tag_removed',
        outcome: 'info',
        metadata: { tag: normalized, invoiceNumber: item.invoiceNumber },
      })
      persistState()
    }
    return item
  },

  async listAllTags(): Promise<string[]> {
    await delay(40)
    const set = new Set<string>()
    for (const inv of state.invoices) {
      for (const t of inv.tags ?? []) set.add(t)
    }
    return Array.from(set).sort()
  },

  // ── AI mockok (dev-only fallback) ────────────────────────────────────
  async aiStatus(): Promise<{ enabled: boolean }> {
    await delay(20)
    return { enabled: false }
  },
  // A visszatérési típust kiírjuk: üres tömb-literálból a TS `never[]`-et következtet,
  // ami nem illeszkedik a valódi kliens `{kind, id}[]`-jéhez. (A korábbi cast ezt is elnyelte.)
  async aiAnalyzeInvoice(_invoiceId: string): Promise<{ ok: true; saved: Array<{ kind: string; id: string }> }> {
    await delay(50)
    return { ok: true as const, saved: [] }
  },
  // Az AI-metódusok visszatérési típusát kiírjuk: üres tömb-/null-literálból a TS
  // `never[]` / `null` típust következtet, ami nem illeszkedik a valódi kliens
  // szignatúrájához. A korábbi `as unknown as MockApi` cast ezt is elnyelte.
  async aiListInvoiceSuggestions(_invoiceId: string): Promise<{ suggestions: AiSuggestion[] }> {
    await delay(20)
    return { suggestions: [] }
  },
  async aiAcceptSuggestion(_id: string): Promise<{ ok: true }> {
    await delay(20)
    return { ok: true as const }
  },
  async aiRejectSuggestion(_id: string): Promise<{ ok: true }> {
    await delay(20)
    return { ok: true as const }
  },
  async aiGenerateMonthlySummary(_y: number, _m: number, _c: string): Promise<AiMonthlySummary> {
    await delay(30)
    return { id: 'mock', narrative: '(Mock — AI nincs)', highlights: [], recommendation: null }
  },
  async aiGetMonthlySummary(_y: number, _m: number, _c: string): Promise<{ result: AiSuggestion | null }> {
    await delay(20)
    return { result: null }
  },
  async aiGenerateCashflow(_c: string): Promise<AiCashflow> {
    await delay(30)
    return { id: 'mock', narrative: '(Mock — AI nincs)', alerts: [] }
  },
  async aiGetCashflow(_c: string): Promise<{ result: AiSuggestion | null }> {
    await delay(20)
    return { result: null }
  },
  async aiGenerateAccountantPrep(_y: number, _m: number, _c: string): Promise<AiAccountantPrep> {
    await delay(30)
    return { id: 'mock', questions: [], summary: '(Mock — AI nincs)' }
  },
  async aiGetAccountantPrep(_y: number, _m: number, _c: string): Promise<{ result: AiSuggestion | null }> {
    await delay(20)
    return { result: null }
  },

  /** Mock implementáció — production-ben a fetchClient ezt felülírja. */
  async getInvoiceStats(_companyId: string): Promise<{
    total: number
    bySource: Record<string, number>
    byWorkflowStatus: Record<string, number>
    lastNavSyncAt: string | null
  }> {
    await delay(40)
    const bySource: Record<string, number> = {}
    const byStatus: Record<string, number> = {}
    for (const i of state.invoices) {
      bySource[i.source] = (bySource[i.source] ?? 0) + 1
      byStatus[i.workflowStatus] = (byStatus[i.workflowStatus] ?? 0) + 1
    }
    return { total: state.invoices.length, bySource, byWorkflowStatus: byStatus, lastNavSyncAt: null }
  },

  async uploadInvoice(payload: UploadInvoicePayload): Promise<UploadInvoiceResponse> {
    await delay(700)
    const newId = 'i-upload-' + Math.floor(performance.now()).toString(36)
    const newItem: InvoiceListItem = {
      id: newId,
      invoiceNumber: 'DRAFT-' + newId.slice(-6).toUpperCase(),
      supplierName: payload.file.name.replace(/\.[^.]+$/, ''),
      supplierTaxNumber: null,
      issueDate: new Date().toISOString().slice(0, 10),
      performanceDate: new Date().toISOString().slice(0, 10),
      paymentDueDate: null,
      netAmount: null,
      vatAmount: null,
      grossAmount: null,
      currency: 'HUF',
      source: 'manual',
      countryType: 'UNKNOWN',
      matchingStatus: 'pending',
      matchingScore: null,
      workflowStatus: 'received',
      approvalStatus: 'not_required',
      paymentStatus: 'unpaid',
      hasPdf: true,
      hasNavXml: false,
      category: payload.category ?? null,
      projectCode: payload.projectCode ?? null,
      tags: [],
    }
    state.invoices.unshift(newItem)
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'invoice',
      subjectId: newId,
      action: 'invoice.uploaded',
      outcome: 'success',
      metadata: {
        fileName: payload.file.name,
        sizeBytes: payload.file.size,
        category: payload.category ?? null,
      },
    })
    persistState()
    return {
      invoiceId: newId,
      fileId: 'f-pdf-' + newId,
      extractionStatus: 'pending',
      duplicateSuspect: false,
      duplicateOfInvoiceId: null,
    }
  },

  async listDiscrepancies(type?: DiscrepancyType): Promise<DiscrepancyIssue[]> {
    await delay(120)
    if (!type) return FIXTURES.DISCREPANCIES
    return FIXTURES.DISCREPANCIES.filter((d) => d.type === type)
  },

  // A `companyId` / `year` / `month` a mockban nem szűr (fix fixture), de a
  // szignatúrában BENNE KELL LENNIE: ez a MockApi típus a szerződés, amihez a
  // valódi `fetchClient` illeszkedik. Korábban paraméter nélkül volt, ezért a
  // fetchClient is paraméter nélkül hívta a backendet, és mindig az aktuális
  // hónapot kérdezte — a kiválasztott hónap címkéje alatt.
  async getVatMonthlySummary(_companyId: string, _year: number, _month: number): Promise<VatMonthlySummary> {
    await delay(100)
    return FIXTURES.VAT_SUMMARY
  },

  async getMonthlyExport(_companyId: string, _year: number, _month: number): Promise<AccountantExport> {
    await delay(100)
    return state.exportPackage
  },

  async approveExport(_id: string, notes?: string): Promise<AccountantExport> {
    await delay(180)
    state.exportPackage = {
      ...state.exportPackage,
      status: 'approved',
      approvedAt: new Date().toISOString(),
      accountantNotes: notes ?? state.exportPackage.accountantNotes,
    }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'accountant_export',
      subjectId: state.exportPackage.id,
      action: 'export.approved',
      outcome: 'success',
      metadata: { period: `${state.exportPackage.periodYear}-${String(state.exportPackage.periodMonth).padStart(2, '0')}` },
    })
    persistState()
    return state.exportPackage
  },

  async rejectExport(_id: string, reason: string): Promise<AccountantExport> {
    await delay(150)
    state.exportPackage = { ...state.exportPackage, status: 'rejected' }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'accountant_export',
      subjectId: state.exportPackage.id,
      action: 'export.rejected',
      outcome: 'info',
      metadata: { reason },
    })
    persistState()
    return state.exportPackage
  },

  async sendExport(_id: string, to: string): Promise<AccountantExport> {
    await delay(400)
    state.exportPackage = {
      ...state.exportPackage,
      status: 'sent',
      sentAt: new Date().toISOString(),
      sentToEmail: to,
    }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'accountant_export',
      subjectId: state.exportPackage.id,
      action: 'export.sent',
      outcome: 'success',
      metadata: { to, period: `${state.exportPackage.periodYear}-${String(state.exportPackage.periodMonth).padStart(2, '0')}` },
    })
    persistState()
    return state.exportPackage
  },

  async listJobs(): Promise<JobRun[]> {
    await delay(80)
    return FIXTURES.JOBS
  },

  async listNotifications(unreadOnly = false): Promise<Notification[]> {
    await delay(60)
    return unreadOnly ? state.notifications.filter((n) => !n.readAt) : state.notifications
  },

  async markNotificationRead(id: string): Promise<void> {
    await delay(40)
    const n = state.notifications.find((x) => x.id === id)
    if (n) n.readAt = new Date().toISOString()
  },

  async markAllNotificationsRead(): Promise<void> {
    await delay(60)
    const now = new Date().toISOString()
    state.notifications.forEach((n) => {
      if (!n.readAt) n.readAt = now
    })
  },

  async listInvoiceComments(invoiceId: string): Promise<InvoiceComment[]> {
    await delay(80)
    return [...(state.comments.get(invoiceId) ?? [])]
  },

  async addInvoiceComment(invoiceId: string, body: string, author: User, authorRole: Role | null): Promise<InvoiceComment> {
    await delay(150)
    const trimmed = body.trim()
    if (!trimmed) throw makeProblem(422, 'comment.empty', 'Üres megjegyzést nem rögzítünk')
    const comment: InvoiceComment = {
      id: 'cm-' + Math.random().toString(36).slice(2, 10),
      invoiceId,
      authorId: author.id,
      authorName: author.fullName,
      authorRole,
      body: trimmed,
      createdAt: new Date().toISOString(),
    }
    const existing = state.comments.get(invoiceId) ?? []
    state.comments.set(invoiceId, [...existing, comment])
    audit({
      actorType: 'user',
      actorId: author.id,
      actorName: author.fullName,
      actorRole: authorRole,
      subjectType: 'invoice',
      subjectId: invoiceId,
      action: 'invoice.comment_added',
      outcome: 'success',
      metadata: { bodyPreview: trimmed.slice(0, 80) },
    })
    persistState()
    return comment
  },

  async listInbox(): Promise<EmailInboxItem[]> {
    await delay(80)
    return FIXTURES.INBOX
  },

  async getNavCredentials(companyId: string): Promise<NavCredentials | null> {
    await delay(80)
    return state.navCredentials.get(companyId) ?? null
  },

  async updateNavCredentials(payload: UpdateNavCredentialsPayload): Promise<NavCredentials> {
    await delay(220)
    const existing = state.navCredentials.get(payload.companyId)
    if (!existing) throw makeProblem(404, 'company.not_found', 'A cég nem található')
    const updated: NavCredentials = {
      ...existing,
      navUserLogin: payload.navUserLogin?.trim() || existing.navUserLogin,
      navEnvironment: payload.navEnvironment ?? existing.navEnvironment,
      hasPassword: payload.password ? true : existing.hasPassword,
      hasSigningKey: payload.signingKey ? true : existing.hasSigningKey,
      hasExchangeKey: payload.exchangeKey ? true : existing.hasExchangeKey,
      lastRotatedAt:
        payload.password || payload.signingKey || payload.exchangeKey
          ? new Date().toISOString()
          : existing.lastRotatedAt,
    }
    state.navCredentials.set(payload.companyId, updated)
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'nav_credentials',
      subjectId: payload.companyId,
      action: 'nav.credentials_updated',
      outcome: 'success',
      metadata: {
        env: updated.navEnvironment,
        passwordRotated: !!payload.password,
        signingKeyRotated: !!payload.signingKey,
        exchangeKeyRotated: !!payload.exchangeKey,
      },
    })
    persistState()
    return updated
  },

  async testNavConnection(companyId: string): Promise<NavCredentials> {
    await delay(600)
    const existing = state.navCredentials.get(companyId)
    if (!existing) throw makeProblem(404, 'company.not_found', 'A cég nem található')

    // Validáció — a NAV Online Számla mind a négyet megköveteli.
    const missing: string[] = []
    if (!existing.navUserLogin?.trim()) missing.push('bejelentkezési név')
    if (!existing.hasPassword) missing.push('jelszó')
    if (!existing.hasSigningKey) missing.push('aláíró kulcs')
    if (!existing.hasExchangeKey) missing.push('csere kulcs')

    const now = new Date().toISOString()

    if (missing.length > 0) {
      const errorMessage = `Hiányzó NAV adatok: ${missing.join(', ')}`
      const updated: NavCredentials = {
        ...existing,
        lastPingAt: now,
        lastPingStatus: 'error',
        lastPingError: errorMessage,
      }
      state.navCredentials.set(companyId, updated)
      audit({
        actorType: 'user',
        actorId: state.currentActor?.id ?? null,
        actorName: state.currentActor?.name ?? null,
        actorRole: state.currentActor?.role ?? null,
        subjectType: 'nav_credentials',
        subjectId: companyId,
        action: 'nav.connection_test_failed',
        outcome: 'failure',
        metadata: { reason: 'credentials_missing', missing },
      })
      persistState()
      throw makeProblem(422, 'nav.credentials_missing', errorMessage)
    }

    // Mock módban ezen túl mindig OK — a valódi backend NAV tokenExchange-t hív.
    // Production-ben itt jönne a NAV REST hívás.
    const updated: NavCredentials = {
      ...existing,
      lastPingAt: now,
      lastPingStatus: 'ok',
      lastPingError: null,
      lastUsedAt: now,
    }
    state.navCredentials.set(companyId, updated)
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'nav_credentials',
      subjectId: companyId,
      action: 'nav.connection_tested',
      outcome: 'success',
      metadata: { env: existing.navEnvironment },
    })
    persistState()
    return updated
  },

  async getEmailAccount(): Promise<EmailAccountSettings> {
    await delay(80)
    return state.emailAccount
  },

  async updateEmailAccount(payload: UpdateEmailAccountPayload): Promise<EmailAccountSettings> {
    await delay(220)
    if (payload.address !== undefined && !payload.address.includes('@')) {
      throw makeProblem(422, 'email.invalid', 'Érvénytelen email cím')
    }
    if (payload.imapPort !== undefined && (payload.imapPort < 1 || payload.imapPort > 65535)) {
      throw makeProblem(422, 'email.invalid_port', 'Érvénytelen port')
    }
    state.emailAccount = {
      ...state.emailAccount,
      address: payload.address?.trim() || state.emailAccount.address,
      imapHost: payload.imapHost?.trim() || state.emailAccount.imapHost,
      imapPort: payload.imapPort ?? state.emailAccount.imapPort,
      imapUsername: payload.imapUsername?.trim() || state.emailAccount.imapUsername,
      imapFolder: payload.imapFolder?.trim() || state.emailAccount.imapFolder,
      hasPassword: payload.password ? true : state.emailAccount.hasPassword,
    }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'email_account',
      subjectId: state.emailAccount.id,
      action: 'email.account_updated',
      outcome: 'success',
      metadata: { address: state.emailAccount.address, passwordRotated: !!payload.password },
    })
    persistState()
    return state.emailAccount
  },

  async testEmailConnection(): Promise<EmailAccountSettings> {
    await delay(500)
    state.emailAccount = {
      ...state.emailAccount,
      status: 'active',
      lastSyncAt: new Date().toISOString(),
      errorMessage: null,
    }
    return state.emailAccount
  },

  async listImapFolders(): Promise<ImapFoldersResult> {
    await delay(500)
    state.emailAccount = { ...state.emailAccount, status: 'active', errorMessage: null }
    return { folders: ['INBOX', 'INBOX/Számlák', 'INBOX/Beérkező', 'Sent', 'Archive', 'Junk', 'Trash'] }
  },

  async getOutgoingEmail(): Promise<OutgoingEmailSettings> {
    await delay(60)
    return state.outgoingEmail
  },

  async updateOutgoingEmail(payload: UpdateOutgoingEmailPayload): Promise<OutgoingEmailSettings> {
    await delay(200)
    if (payload.fromAddress !== undefined && payload.fromAddress.length > 0 && !payload.fromAddress.includes('@')) {
      throw makeProblem(422, 'outgoing.invalid_from', 'Érvénytelen feladó email cím')
    }
    if (payload.replyToAddress && payload.replyToAddress.length > 0 && !payload.replyToAddress.includes('@')) {
      throw makeProblem(422, 'outgoing.invalid_replyto', 'Érvénytelen reply-to email cím')
    }
    if (payload.smtpPort !== undefined && (payload.smtpPort < 1 || payload.smtpPort > 65535)) {
      throw makeProblem(422, 'outgoing.invalid_port', 'Érvénytelen SMTP port')
    }
    state.outgoingEmail = {
      ...state.outgoingEmail,
      provider: payload.provider ?? state.outgoingEmail.provider,
      fromAddress: payload.fromAddress ?? state.outgoingEmail.fromAddress,
      fromName: payload.fromName !== undefined ? payload.fromName : state.outgoingEmail.fromName,
      replyToAddress:
        payload.replyToAddress !== undefined ? payload.replyToAddress : state.outgoingEmail.replyToAddress,
      smtpHost: payload.smtpHost ?? state.outgoingEmail.smtpHost,
      smtpPort: payload.smtpPort ?? state.outgoingEmail.smtpPort,
      smtpUsername: payload.smtpUsername ?? state.outgoingEmail.smtpUsername,
      hasPassword: payload.password ? true : state.outgoingEmail.hasPassword,
      encryption: payload.encryption ?? state.outgoingEmail.encryption,
      enabled: payload.enabled !== undefined ? payload.enabled : state.outgoingEmail.enabled,
    }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'outgoing_email',
      subjectId: state.outgoingEmail.id,
      action: 'outgoing_email.updated',
      outcome: 'success',
      metadata: {
        provider: state.outgoingEmail.provider,
        fromAddress: state.outgoingEmail.fromAddress,
        enabled: state.outgoingEmail.enabled,
        passwordRotated: !!payload.password,
      },
    })
    persistState()
    return state.outgoingEmail
  },

  async testOutgoingEmail(): Promise<OutgoingEmailSettings> {
    await delay(500)
    const cur = state.outgoingEmail
    const missing: string[] = []
    if (!cur.fromAddress.trim()) missing.push('feladó email')
    if (cur.provider === 'smtp') {
      if (!cur.smtpHost?.trim()) missing.push('SMTP host')
      if (!cur.smtpUsername?.trim()) missing.push('SMTP felhasználónév')
      if (!cur.hasPassword) missing.push('SMTP jelszó')
    }
    const now = new Date().toISOString()

    if (missing.length > 0) {
      const errorMessage = `Hiányzó SMTP adatok: ${missing.join(', ')}`
      state.outgoingEmail = {
        ...cur,
        lastTestAt: now,
        lastTestStatus: 'error',
        lastTestError: errorMessage,
      }
      audit({
        actorType: 'user',
        actorId: state.currentActor?.id ?? null,
        actorName: state.currentActor?.name ?? null,
        actorRole: state.currentActor?.role ?? null,
        subjectType: 'outgoing_email',
        subjectId: cur.id,
        action: 'outgoing_email.test_failed',
        outcome: 'failure',
        metadata: { missing },
      })
      persistState()
      throw makeProblem(422, 'outgoing.incomplete', errorMessage)
    }

    state.outgoingEmail = {
      ...cur,
      lastTestAt: now,
      lastTestStatus: 'ok',
      lastTestError: null,
    }
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'outgoing_email',
      subjectId: cur.id,
      action: 'outgoing_email.test_ok',
      outcome: 'success',
      metadata: { provider: cur.provider },
    })
    persistState()
    return state.outgoingEmail
  },

  async getCompanySettings(companyId: string): Promise<CompanySettings | null> {
    await delay(80)
    return state.companySettings.get(companyId) ?? null
  },

  async updateCompanySettings(
    companyId: string,
    payload: UpdateCompanySettingsPayload,
  ): Promise<CompanySettings> {
    await delay(220)
    const existing = state.companySettings.get(companyId)
    if (!existing) throw makeProblem(404, 'company.not_found', 'A cég nem található')
    if (payload.accountantEmail !== undefined && payload.accountantEmail.length > 0 && !payload.accountantEmail.includes('@')) {
      throw makeProblem(422, 'accountant.invalid_email', 'Érvénytelen email cím')
    }
    const updated: CompanySettings = {
      ...existing,
      accountantName: payload.accountantName?.trim() ?? existing.accountantName,
      accountantEmail: payload.accountantEmail?.trim() ?? existing.accountantEmail,
      exportFormat: payload.exportFormat ?? existing.exportFormat,
      vatPeriod: payload.vatPeriod ?? existing.vatPeriod,
      monthlyExportDay: payload.monthlyExportDay ?? existing.monthlyExportDay,
      monthlyExportHour: payload.monthlyExportHour ?? existing.monthlyExportHour,
      defaultExchangeRateSource: payload.defaultExchangeRateSource ?? existing.defaultExchangeRateSource,
    }
    state.companySettings.set(companyId, updated)
    audit({
      actorType: 'user',
      actorId: state.currentActor?.id ?? null,
      actorName: state.currentActor?.name ?? null,
      actorRole: state.currentActor?.role ?? null,
      subjectType: 'company_settings',
      subjectId: companyId,
      action: 'settings.accountant_updated',
      outcome: 'success',
      metadata: { accountantEmail: updated.accountantEmail, vatPeriod: updated.vatPeriod },
    })
    persistState()
    return updated
  },

  async triggerEmailSync(): Promise<JobResponse> {
    await delay(200)
    return {
      jobId: 'j-email-trigger-' + Date.now(),
      status: 'skipped',
      detail: 'Mock módban IMAP-sync nincs (backend kell hozzá).',
    }
  },

  async triggerNavSync(range?: { from: string; to: string }): Promise<JobResponse> {
    await delay(200)
    return {
      jobId: 'j-nav-trigger-' + Date.now(),
      status: 'skipped',
      detail: range
        ? `Mock módban NAV-sync nincs (backend kell hozzá). Kért tartomány: ${range.from} … ${range.to}.`
        : 'Mock módban NAV-sync nincs (backend kell hozzá).',
    }
  },

  // V1.2 — csak production-ben hasznosak; mock módban informatív "skipped" választ adunk.

  async recordPayment(invoiceId: string, payload: { status: string; paidAmount?: number; paidAt?: string; method?: string; reference?: string }): Promise<RecordPaymentResponse> {
    await delay(120)
    const item = state.invoices.find((i) => i.id === invoiceId)
    if (!item) throw makeProblem(404, 'invoice.not_found', 'A számla nem található')
    item.paymentStatus = payload.status as typeof item.paymentStatus
    if (payload.status === 'paid' && (item.workflowStatus === 'approved' || item.workflowStatus === 'approval_pending')) {
      item.workflowStatus = 'paid'
    }
    persistState()
    return { ok: true, paymentStatus: item.paymentStatus, workflowStatus: item.workflowStatus }
  },

  async downloadFromLink(_payload: { companyId: string; url: string }): Promise<LinkDownloadResponse> {
    await delay(300)
    throw makeProblem(501, 'link.not_in_mock', 'Link-letöltés csak production backend-en érhető el.')
  },

  async extractInvoice(_invoiceId: string): Promise<ExtractResponse> {
    await delay(100)
    // A `fields` teljes alakzatát ki kell adni — a korábbi `{}` a szerződést sértette,
    // csak a mockból származó típus miatt nem derült ki.
    return {
      ok: false,
      fields: {
        supplierName: null,
        supplierTaxNumber: null,
        buyerName: null,
        buyerTaxNumber: null,
        invoiceNumber: null,
        issueDate: null,
        performanceDate: null,
        paymentDueDate: null,
        netAmount: null,
        vatAmount: null,
        grossAmount: null,
        currency: null,
        vatRatePercent: null,
        vatCategory: null,
        extractionConfidence: 0,
      },
    }
  },

  async getSzamlazz(companyId: string): Promise<SzamlazzCredentialsStatus> {
    await delay(60)
    return {
      companyId,
      hasAgentKey: false,
      enabled: false,
      lastUsedAt: null,
      lastTestAt: null,
      lastTestStatus: null,
      lastTestError: null,
    }
  },

  async updateSzamlazz(companyId: string, _payload: { agentKey?: string; enabled?: boolean }): Promise<SzamlazzCredentialsStatus> {
    await delay(100)
    return {
      companyId,
      hasAgentKey: false,
      enabled: false,
      lastUsedAt: null,
      lastTestAt: null,
      lastTestStatus: null,
      lastTestError: null,
    }
  },

  async testSzamlazz(_companyId: string): Promise<{ ok: false; detail: string }> {
    await delay(100)
    return { ok: false, detail: 'Mock módban Számlázz.hu nincs (backend kell hozzá).' }
  },

  async syncSzamlazz(_companyId: string): Promise<JobResponse> {
    await delay(100)
    return {
      jobId: 'j-szl-' + Date.now(),
      status: 'skipped',
      detail: 'Mock módban Számlázz.hu sync nincs (backend kell hozzá).',
    }
  },

  async updateCostAllocation(
    _invoiceId: string,
    _payload: unknown,
  ): Promise<{ ok: true; splits: never[] }> {
    await delay(100)
    return { ok: true, splits: [] }
  },

  async bulkInvoiceAction(_payload: unknown): Promise<{ ok: number; fail: number; results: never[] }> {
    await delay(50)
    return { ok: 0, fail: 0, results: [] }
  },

  async deleteInvoice(_id: string): Promise<{ ok: true }> {
    await delay(50)
    return { ok: true }
  },

  async getCronStatus(): Promise<{ jobs: never[] }> {
    await delay(50)
    return { jobs: [] }
  },

  async getBreakdownTrend(
    _companyId: string,
    _months: number,
  ): Promise<{ months: never[]; categories: never[]; topSuppliers: never[]; projectProfitability: never[] }> {
    await delay(50)
    return { months: [], categories: [], topSuppliers: [], projectProfitability: [] }
  },

  async runCronJob(_type: string): Promise<{ ok: true; summary: string }> {
    await delay(50)
    return { ok: true, summary: 'Mock módban a cron nem fut.' }
  },

  async getCronRuns(_limit = 30): Promise<{ runs: never[] }> {
    await delay(50)
    return { runs: [] }
  },

  async getHealthIntegrations(): Promise<HealthIntegrationsResponse> {
    await delay(50)
    return { integrations: [], counters: { overdueCount: 0, pendingApprovalCount: 0, discrepancyCount: 0 } }
  },

  async listBankTransactions(_c: string, _u = false): Promise<{ items: never[] }> {
    await delay(50)
    return { items: [] }
  },

  async importBankCsv(_c: string, _f: File): Promise<BankingImportResponse> {
    await delay(50)
    return { ok: true, total: 0, inserted: 0, duplicate: 0, autoMatched: 0, errors: [] }
  },

  async importBankPdf(_c: string, _f: File): Promise<{ ok: true; total: 0; inserted: 0; duplicate: 0; autoMatched: 0; errors: never[] }> {
    await delay(50)
    return { ok: true, total: 0, inserted: 0, duplicate: 0, autoMatched: 0, errors: [] }
  },

  async manualMatchBankTransaction(_t: string, _i: string): Promise<{ ok: true }> {
    await delay(50)
    return { ok: true }
  },

  async unmatchBankTransaction(_t: string): Promise<{ ok: true }> {
    await delay(50)
    return { ok: true }
  },

  async deleteBankTransaction(_t: string): Promise<{ ok: true; deleted: number; revertedInvoices: number }> {
    await delay(50)
    return { ok: true, deleted: 1, revertedInvoices: 0 }
  },

  async deleteBankTransactions(ids: string[]): Promise<{ ok: true; deleted: number; revertedInvoices: number }> {
    await delay(80)
    return { ok: true, deleted: ids.length, revertedInvoices: 0 }
  },

  async rematchBankTransactions(): Promise<{ ok: true; scanned: number; matched: number }> {
    await delay(120)
    return { ok: true, scanned: 0, matched: 0 }
  },

  async getBreakdown(
    _companyId: string,
    year: number,
    month: number,
  ): Promise<{
    period: { year: number; month: number }
    invoiceCount: number
    totalGross: number
    byProject: []
    byUser: []
    byPaidBy: {
      company: { count: 0; gross: 0 }
      personal: { count: 0; gross: 0 }
      unset: { count: 0; gross: 0 }
    }
    personalRefund: []
  }> {
    await delay(100)
    return {
      period: { year, month },
      invoiceCount: 0,
      totalGross: 0,
      byProject: [],
      byUser: [],
      byPaidBy: { company: { count: 0, gross: 0 }, personal: { count: 0, gross: 0 }, unset: { count: 0, gross: 0 } },
      personalRefund: [],
    }
  },
}

/**
 * Az API-szerződés a **valódi** kliensből származik, nem a mockból.
 *
 * Korábban `typeof mockApi` volt, ami visszafelé kötötte a függést: a production
 * kliensnek a mock véletlen típus-következtetéseihez kellett idomulnia (üres tömbből
 * `never[]`, egy `status: 'skipped'` literálból szűk unió, `null`-ból `null` típus).
 * Ezt csak egy `as unknown as MockApi` cast tudta elnyelni — és ugyanaz a cast rejtette
 * el az igazi hibát is: a `getVatMonthlySummary`/`getMonthlyExport` a valódi kliensben
 * paraméter nélkül volt és `new Date()`-tel dolgozott.
 *
 * Így viszont a mock szűkebb típusai gond nélkül illeszkednek (a `never[]` bármely
 * tömbnek megfelel), a valódi kliens deklarált típusai pedig a szerződés.
 */
export type MockApi = typeof fetchApi

function makeProblem(status: number, code: string, detail?: string) {
  const err = new Error(detail ?? code) as Error & { problem: unknown }
  err.problem = { type: 'about:blank', title: code, status, code, detail }
  return err
}

/**
 * API switch — production build-ben MINDIG a valódi backend.
 * Dev build-ben (Vite serve), ha `VITE_API_BASE_URL` env be van állítva (akár üresen is),
 * akkor szintén a fetchApi, különben a mock. Production-ben nincs mock-fallback.
 *
 * Tudatos döntés: a `import.meta.env.PROD` Vite build-time constant — production build-ben
 * `true`-ra értékelődik és tree-shake-eli a mockApi-t. Így nem lehet véletlenül mock módba kerülni
 * éles deploy-on.
 */
import { fetchApi, isRealBackend } from './fetchClient'
const usingReal = import.meta.env.PROD || isRealBackend
if (typeof window !== 'undefined') {
  // Boot diagnostika — production debug-hoz látható a böngésző konzolban.
  // eslint-disable-next-line no-console
  console.log(
    `[adaptivo-szamla] API mode: ${usingReal ? 'REAL backend' : 'MOCK'} (prod=${import.meta.env.PROD}, base=${JSON.stringify(import.meta.env.VITE_API_BASE_URL)})`,
  )
  if (usingReal) {
    // Tisztítás: ha a böngészőben még benne van a régi mock-mode állapot
    // (`adaptivo.szamla.mockState.v2` és a hozzátartozó session JWT nélkül), törlés.
    // Ez egyszer fut a deploy után — anélkül a user "Kele Róbert"-ként látja magát
    // logged-in-ként, pedig a backend nem ismeri.
    try {
      window.localStorage.removeItem('adaptivo.szamla.mockState.v1')
      window.localStorage.removeItem('adaptivo.szamla.mockState.v2')
      const hasSession = !!window.localStorage.getItem('adaptivo.szamla.session')
      const hasToken = !!window.localStorage.getItem('adaptivo.szamla.token')
      if (hasSession && !hasToken) {
        // Mock-szerű session JWT token nélkül → invalid. Töröljük hogy a /login-ra menjen.
        window.localStorage.removeItem('adaptivo.szamla.session')
        window.localStorage.removeItem('adaptivo.szamla.activeCompany')
        // eslint-disable-next-line no-console
        console.log('[adaptivo-szamla] Stale mock session törölve — login szükséges.')
      }
    } catch {
      // ignore (SecurityError / quota stb.)
    }
  }
}
// NINCS `as unknown as MockApi` cast: az elnyelte a szignatúra-eltéréseket a mock és a
// valódi kliens között. Pont ez rejtette el, hogy a `getVatMonthlySummary`/`getMonthlyExport`
// a fetchClientben paraméter nélkül volt és `new Date()`-tel dolgozott. A cast nélkül a
// fordító hitelesíti, hogy a két implementáció ugyanazt a szerződést teljesíti.
export const api: MockApi = usingReal ? fetchApi : mockApi

/** Visszaadja a felhasználó nevét (login emaillel) — fejlesztéshez. */
export function whoAmIByEmail(email: string): User | undefined {
  return Object.values(FIXTURES.USERS).find((u) => u.email === email)
}
